/**
 * useMinhaMesaRibbon — Ribbon config for Minha Mesa (MasterRibbon pattern)
 *
 * Groups:
 *  "Fila"    (left)  : [Iniciar]  [Arquivo ▾]
 *  "Exibir"  (center): [Exibir ▾]  → Lista | Kanban | Linha do Tempo
 *
 * slots.afterGroups → [Todos] [Filtros] [FIFO]
 */
import { useMemo } from 'react';
import {
  Play, Folder, ClipboardList, BarChart3,
  List, LayoutGrid, GanttChart, Plus,
  Eye, BookOpen, History,
} from 'lucide-react';
import type { SupportPanelTab } from './useUnifiedPanelBadges';
import type { RibbonConfig } from '../../../types/ribbon';

const NOOP = () => {};

export type MinhaMesaViewMode = 'list' | 'kanban' | 'timeline';
export type QueueSortMode = 'score' | 'risk' | 'tp';

export interface MinhaMesaRibbonDeps {
  canPullNext?: boolean;
  cockpitActive?: boolean;
  onPullNextTask?: () => void;
  /** Abre a Gaveta Retrátil de Nova Solicitação (Prompt 31). */
  onOpenLauncher?: () => void;
  requestCatalogOpen?: boolean;
  workViewMode: MinhaMesaViewMode;
  onSetWorkViewMode: (mode: MinhaMesaViewMode) => void;
  historyActive?: boolean;
  onToggleHistory?: () => void;
  onExportProductivity?: () => void;
  queueSortMode?: QueueSortMode;
  onQueueSortModeChange?: (mode: QueueSortMode) => void;
  filtersWidget?: React.ReactNode;
  supportTab?: SupportPanelTab;
  supportPanelOpen?: boolean;
  onSupportTabChange?: (tab: SupportPanelTab) => void;
  supportSopDisabled?: boolean;
  supportHistoricoDisabled?: boolean;
}

export function useMinhaMesaRibbon(deps: MinhaMesaRibbonDeps): RibbonConfig {
  const showPull = !deps.cockpitActive && !!deps.onPullNextTask && !!deps.canPullNext;

  const viewLabel =
    deps.workViewMode === 'kanban'
      ? 'Kanban'
      : deps.workViewMode === 'timeline'
        ? 'Linha do Tempo'
        : 'Lista';

  return useMemo<RibbonConfig>(() => ({
    moduleId: 'minha-mesa',
    groups: [
      // ─── Fila (LEFT) ────────────────────────────
      {
        id: 'fila',
        label: 'Fila',
        position: 'left',
        actions: [
          {
            id: 'pull-next',
            label: 'Iniciar',
            icon: Play,
            onClick: deps.onPullNextTask ?? NOOP,
            variant: 'primary',
            hidden: !showPull,
          },
          {
            id: 'arquivo',
            label: 'Arquivo',
            icon: Folder,
            onClick: NOOP,
            dropdown: [
              {
                id: 'history',
                label: 'Histórico de Conclusões',
                icon: ClipboardList,
                onClick: deps.onToggleHistory ?? NOOP,
              },
              { id: 'div1', label: '', onClick: NOOP, divider: true },
              {
                id: 'export-productivity',
                label: 'Relatório de Produtividade',
                icon: BarChart3,
                onClick: deps.onExportProductivity ?? NOOP,
              },
            ],
          },
        ],
      },
      // ─── Exibir (CENTER) ─────────────────────────
      {
        id: 'exibir',
        label: 'Exibir',
        position: 'center',
        actions: [
          {
            id: 'exibir',
            label: 'Exibir',
            icon: Eye,
            onClick: NOOP,
            active: true,
            tooltip: viewLabel,
            dropdown: [
              {
                id: 'view-list',
                label: 'Lista',
                icon: List,
                onClick: () => deps.onSetWorkViewMode('list'),
                description: deps.workViewMode === 'list' ? '●' : undefined,
              },
              {
                id: 'view-kanban',
                label: 'Kanban',
                icon: LayoutGrid,
                onClick: () => deps.onSetWorkViewMode('kanban'),
                description: deps.workViewMode === 'kanban' ? '●' : undefined,
              },
              {
                id: 'view-timeline',
                label: 'Linha do Tempo',
                icon: GanttChart,
                onClick: () => deps.onSetWorkViewMode('timeline'),
                description: deps.workViewMode === 'timeline' ? '●' : undefined,
              },
            ],
          },
          {
            id: 'nova-solicitacao',
            label: 'Nova',
            icon: Plus,
            onClick: deps.onOpenLauncher ?? NOOP,
            disabled: !deps.onOpenLauncher,
            active: Boolean(deps.requestCatalogOpen),
            tooltip: deps.requestCatalogOpen ? 'Fechar nova solicitação' : 'Nova solicitação',
            hidden: !deps.onOpenLauncher,
          },
          {
            id: 'painel-carteira',
            label: 'Carteira',
            icon: ClipboardList,
            onClick: () => deps.onSupportTabChange?.('demandas'),
            active: !deps.requestCatalogOpen && deps.workViewMode === 'list' && Boolean(deps.supportPanelOpen) && deps.supportTab === 'demandas',
            hidden: deps.workViewMode !== 'list' || !deps.onSupportTabChange,
            tooltip: deps.supportPanelOpen && deps.supportTab === 'demandas' ? 'Ocultar carteira' : 'Exibir carteira',
          },
          {
            id: 'painel-guia',
            label: 'Guia',
            icon: BookOpen,
            onClick: () => deps.onSupportTabChange?.('sop'),
            active: deps.workViewMode === 'list' && Boolean(deps.supportPanelOpen) && deps.supportTab === 'sop',
            disabled: deps.supportSopDisabled,
            hidden: deps.workViewMode !== 'list' || !deps.onSupportTabChange,
            tooltip: 'Guia SOP da demanda selecionada',
          },
          {
            id: 'painel-historico',
            label: 'Histórico',
            icon: History,
            onClick: () => deps.onSupportTabChange?.('historico'),
            active: deps.workViewMode === 'list' && Boolean(deps.supportPanelOpen) && deps.supportTab === 'historico',
            disabled: deps.supportHistoricoDisabled,
            hidden: deps.workViewMode !== 'list' || !deps.onSupportTabChange,
            tooltip: 'Histórico da demanda selecionada',
          },
        ],
      },
    ],
    slots: {
      afterGroups: deps.filtersWidget,
    },
  }), [deps, showPull, viewLabel]);
}
